import { useEffect, RefObject } from 'react';
import { useAppStore } from '../store/useAppStore';
import { SelectedLayerKf, LayerKfClipboardItem } from '../components/timeline/timelineConstants';

// ── Props ─────────────────────────────────────────────────────────────────────

interface UseTimelineKeyboardOptions {
  selectedKfs:            SelectedLayerKf[];
  kfClipboardRef:         RefObject<LayerKfClipboardItem[]>;
  copySelectedKfs:        () => void;
  pasteSelectedKfsAtFrame:(frame: number) => void;
  deleteSelectedKfs:      () => void;
  clearSelectedKfs:       () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTimelineKeyboard({
  selectedKfs,
  kfClipboardRef,
  copySelectedKfs,
  pasteSelectedKfsAtFrame,
  deleteSelectedKfs,
  clearSelectedKfs,
}: UseTimelineKeyboardOptions): void {
  const setInPoint  = useAppStore((s) => s.setInPoint);
  const setOutPoint = useAppStore((s) => s.setOutPoint);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.closest('input, textarea, select') || target.isContentEditable)) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (!selectedKfs.length) return;
        e.preventDefault();
        copySelectedKfs();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (!kfClipboardRef.current?.length) return;
        e.preventDefault();
        pasteSelectedKfsAtFrame(Math.round(useAppStore.getState().currentFrame));
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!selectedKfs.length) return;
        e.preventDefault();
        deleteSelectedKfs();
        return;
      }

      if (e.key === 'Escape') {
        if (!selectedKfs.length) return;
        e.preventDefault();
        clearSelectedKfs();
        return;
      }

      // In/out point shortcuts
      if (!e.ctrlKey && !e.metaKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        if (e.shiftKey) {
          setInPoint(null);
        } else {
          setInPoint(Math.round(useAppStore.getState().currentFrame));
        }
        return;
      }
      if (!e.ctrlKey && !e.metaKey && (e.key === 'o' || e.key === 'O')) {
        e.preventDefault();
        if (e.shiftKey) {
          setOutPoint(null);
        } else {
          setOutPoint(Math.round(useAppStore.getState().currentFrame));
        }
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelectedKfs, copySelectedKfs, deleteSelectedKfs, kfClipboardRef, pasteSelectedKfsAtFrame, selectedKfs, setInPoint, setOutPoint]);
}
